export enum KbDocumentStatus {
  Uploaded = "Uploaded",
  Processing = "Processing",
  Indexed = "Indexed",
  Failed = "Failed",
  Deleted = "Deleted",
}

export interface KbDocumentDto {
  id: string;
  fileName: string;
  contentType?: string;
  fileSizeBytes?: number;
  status: KbDocumentStatus;
  errorMessage?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  isGlobal: boolean;
  folderId?: string;
  folderName?: string;
}

export interface DocumentStatusDto {
  id: string;
  status: KbDocumentStatus;
  progress: number;
  errorMessage?: string;
  updatedAt: string;
}

export interface KbIngestionRunDto {
  id: string;
  trigger: string;
  status: string;
  bedrockJobId?: string;
  documentsProcessed: number;
  documentsFailed: number;
  startedAt: string;
  completedAt?: string;
  errorMessage?: string;
}

export interface GenerateUploadUrlRequest {
  fileName: string;
  contentType: string;
}

export interface UploadUrlResponse {
  docId: string;
  presignedUrl: string;
  s3Key: string;
  expiresIn: number;
}

export interface ConfirmUploadRequest {
  docId: string;
  fileName: string;
  contentType: string;
  s3Key: string;
  folderId?: string;
}

export interface KbStatsDto {
  totalDocuments: number;
  indexedDocuments: number;
  processingDocuments: number;
  failedDocuments: number;
  totalWebSources: number;
  activeWebSources: number;
  syncingWebSources: number;
  failedWebSources: number;
  lastIngestionRun?: KbIngestionRunDto;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface GetDocumentsParams {
  search?: string;
  status?: KbDocumentStatus;
  page?: number;
  pageSize?: number;
  folderId?: string | null;
}

// Test Retrieval Types
export interface KbTestRetrievalRequest {
  query: string;
  topK?: number;
}

export interface KbTestRetrievalResponse {
  success: boolean;
  errorMessage?: string;
  chunks: KbRetrievedChunkDto[];
  totalChunks: number;
}

export interface KbRetrievedChunkDto {
  content: string;
  score: number;
  sourceFileName?: string;
  metadata?: Record<string, string>;
}

// Web Source Types
export enum KbWebSourceStatus {
  Pending = "Pending",
  Syncing = "Syncing",
  Active = "Active",
  Failed = "Failed",
  Deleted = "Deleted",
}

export interface KbWebSourceDto {
  id: string;
  name: string;
  seedUrls: string[];
  status: KbWebSourceStatus;
  maxPages: number;
  rateLimit: number;
  scope: "HOST_ONLY" | "SUBDOMAINS";
  inclusionFilters?: string[];
  exclusionFilters?: string[];
  pagesDiscovered?: number;
  pagesIndexed?: number;
  errorMessage?: string;
  createdAt: string;
  lastSyncedAt?: string;
  isGlobal: boolean;
  folderId?: string;
  folderName?: string;
}

// Web source page types

/** Whether a crawled URL reached the knowledge base. */
export type KbWebSourcePageStatus =
  | "Indexed"
  | "Skipped"
  | "Failed"
  | "Syncing"
  /** Excluded from the knowledge base by an admin; stays out of future crawls. */
  | "Excluded";

/** Why a crawled URL did not reach the knowledge base. Absent when indexed. */
export type KbWebSourcePageReason =
  | "BudgetReached"
  | "TooLittleContent"
  | "Duplicate"
  | "HttpError"
  | "RequestFailed"
  | "UnsupportedContentType";

export interface KbWebSourcePageDto {
  id: string;
  /** The URL the crawler fetched (may be a `.md` alias). */
  url: string;
  /** Human-facing page URL to display and link to. */
  displayUrl: string;
  title?: string;
  status: KbWebSourcePageStatus;
  reason?: KbWebSourcePageReason;
  statusCode?: number;
  contentLength?: number;
  contentType?: string;
  crawledAt?: string;
}

export interface WebSourcePagesSummary {
  indexed: number;
  skipped: number;
  failed: number;
  /** Pages an admin excluded from the knowledge base. */
  excluded: number;
  /** Pages discovered but omitted from the list to stay within the crawler's cap. */
  truncated: number;
}

export interface WebSourcePagesResponse {
  pages: KbWebSourcePageDto[];
  summary: WebSourcePagesSummary;
}

// Folder types
export interface KbFolderDto {
  id: string;
  name: string;
  parentFolderId?: string;
  documentCount: number;
  webSourceCount: number;
  isGlobal: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface KbFolderTreeDto extends KbFolderDto {
  children: KbFolderTreeDto[];
}

export type KbViewMode = "all" | "documents" | "webSources";

export interface CreateFolderRequest {
  name: string;
  parentFolderId?: string;
}

export interface RenameFolderRequest {
  name: string;
}

export interface MoveItemRequest {
  folderId: string | null;
}

export interface CreateWebSourceRequest {
  name: string;
  seedUrls: string[];
  maxPages?: number;
  rateLimit?: number;
  scope?: "HOST_ONLY" | "SUBDOMAINS";
  inclusionFilters?: string[];
  exclusionFilters?: string[];
  isGlobal?: boolean;
  folderId?: string;
}

export interface UpdateWebSourceRequest {
  name?: string;
  seedUrls?: string[];
  maxPages?: number;
  rateLimit?: number;
  scope?: "HOST_ONLY" | "SUBDOMAINS";
  inclusionFilters?: string[];
  exclusionFilters?: string[];
  isGlobal?: boolean;
}
